/**
 * /server-health (OCP-348) — live host-level server health from the node
 * agent (real host CPU/RAM/disk + containers), distinct from the
 * orboto-instance telemetry dashboard. Grouped by server role
 * (tenant pool vs storage/infra); red on silent / over-threshold.
 */
import { useCallback, useEffect, useState } from 'react';

import { api, ApiError } from '../lib/api';
import { formatRelative } from './HomePage';
import { useTransientState } from '../lib/useTransientState';

interface FleetRow {
  id: string;
  // OCP-442 - friendly display name (nullable); the card leads with this and
  // falls back to hostname, showing the real hostname as muted secondary text.
  name: string | null;
  hostname: string;
  region: string;
  server_role?: string;
  serverRole: string;
  agentProvisioned: boolean;
  snapshotAt: string | null;
  silent: boolean;
  cpuPct: number | null;
  ioWaitPct: number | null;
  // OCP-468 - disk saturation (busiest physical disk's io_time-busy %util over
  // the last beacon interval). The reliable "disk saturated" signal, unlike the
  // noisy sub-second iowait. Null on old agents / the agent's priming beacon.
  diskUtilPct: number | null;
  load1: number | null;
  // OCP-642 - load1 / cpu_cores (the unit the sustained load alert uses),
  // swap size + used share, and PSI memory pressure (`full avg60`, %). All
  // null on old agents / a kernel without PSI / a box without swap.
  cpuCores?: number | null;
  loadPerCore?: number | null;
  ramPct: number | null;
  ramUsedMb: number | null;
  ramTotalMb: number | null;
  swapTotalMb?: number | null;
  swapUsedMb?: number | null;
  swapPct?: number | null;
  memPressureFullAvg60?: number | null;
  diskPct: number | null;
  diskUsedGb: number | null;
  diskTotalGb: number | null;
  // OCP-367 — every mount; diskPct above is the worst of these.
  disks?: DiskMount[];
  // OCP-622 - per-GPU samples (model host); gpuVramPct = the worst card.
  gpus?: Array<{ index: number; name: string; vram_used_mb: number; vram_total_mb: number; util_pct?: number | null; temp_c?: number | null }>;
  gpuVramPct?: number | null;
  containerCount: number | null;
  containerRunning: number | null;
  // OCP-464 - live NIC throughput + FairFlat month-to-date volume.
  uplinkMbits: number | null;
  nicRxMbits: number | null;
  nicTxMbits: number | null;
  nicRxMbitsPeak: number | null;
  nicTxMbitsPeak: number | null;
  nicTxPct: number | null;
  volumeCycleMonth: string | null;
  volumeTotalBytes: number | null;
  volumeIncludedGb: number;
  volumePct: number | null;
  // OCP-439 - per-tenant load (heaviest first) + the system/unattributed bucket.
  tenants?: TenantLoad[];
  unattributed?: UnattributedLoad | null;
}

// OCP-439 - one tenant's load aggregated over its containers on this server.
interface TenantLoad {
  tenantId: string;
  subdomain: string | null;
  containerCount: number;
  cpuPct: number;
  ramUsedMb: number;
}

// OCP-439 - system/other containers (coolify-proxy, maintenance, OCP itself).
interface UnattributedLoad {
  containerCount: number;
  cpuPct: number;
  ramUsedMb: number;
}

interface DiskMount {
  mount: string;
  fs_type?: string;
  total_gb: number;
  used_gb: number;
  used_pct?: number;
  inode_pct?: number;
}

// OCP-433/442 - display precedence: lead with the friendly `name` when set,
// else the real hostname. Mirrors serverLabel in AdminServersPage.
function serverLabel(s: { name: string | null; hostname: string }): string {
  return s.name?.trim() || s.hostname;
}

const ROLE_GROUPS: { role: string; label: string }[] = [
  { role: 'tenant', label: 'Tenant pool' },
  { role: 'storage', label: 'Storage' },
  { role: 'infra', label: 'Infra' },
  { role: 'ai-gateway', label: 'AI gateway' },
  { role: 'ai-backend', label: 'AI backend (model host)' },
  { role: 'control-plane', label: 'Control plane (OCP host)' },
];

interface InstallState {
  name: string | null;
  hostname: string;
  command: string;
}

interface PoolRow {
  region: string;
  poolTag: string;
  serverCount: number;
  usedSlots: number;
  capSlots: number;
  slotPct: number | null;
  ramPct: number | null;
  // OCP-465 - fixed-inventory pools (demo) are excluded from the acquisition
  // alert; `alerting` is the persisted edge-trigger state.
  excluded?: boolean;
  alerting?: boolean;
  lastAlertedAt?: string | null;
}

// Pool-capacity warn threshold fallback; the API returns the configured
// monitoring.pool_capacity_warn_pct so the UI colours against the live value.
const POOL_WARN_PCT = 75;

export function ServerHealthPage() {
  const [items, setItems] = useState<FleetRow[] | null>(null);
  const [pools, setPools] = useState<PoolRow[] | null>(null);
  const [poolWarnPct, setPoolWarnPct] = useState<number>(POOL_WARN_PCT);
  const [error, setError] = useState<string | null>(null);
  const [install, setInstall] = useState<InstallState | null>(null);
  const [installError, setInstallError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [fleet, poolRes] = await Promise.all([
        api<{ items: FleetRow[] }>('/v1/admin/host-metrics/fleet'),
        api<{ items: PoolRow[]; warnPct?: number }>('/v1/admin/host-metrics/pools'),
      ]);
      setItems(fleet.items);
      setPools(poolRes.items);
      if (typeof poolRes.warnPct === 'number' && poolRes.warnPct > 0) setPoolWarnPct(poolRes.warnPct);
    } catch (err) {
      setError(err instanceof ApiError ? `Could not load (HTTP ${err.status}).` : 'Could not load.');
    }
  }, []);

  const onInstall = useCallback(async (row: FleetRow) => {
    setInstallError(null);
    try {
      const r = await api<{ install_command: string }>(
        `/v1/admin/vps-servers/${row.id}/agent-secret`,
        { method: 'POST' },
      );
      setInstall({ name: row.name, hostname: row.hostname, command: r.install_command });
    } catch (err) {
      setInstallError(
        err instanceof ApiError ? `Could not generate (HTTP ${err.status}).` : 'Could not generate.',
      );
    }
  }, []);

  useEffect(() => {
    void load();
    // Auto-refresh every 30s for a live feel.
    const t = window.setInterval(() => void load(), 30_000);
    return () => window.clearInterval(t);
  }, [load]);

  return (
    <div className="mx-auto max-w-5xl">
      <header className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-baseline sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Server health</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Live host metrics from the node agent. Auto-refreshes every 30 s.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          className="self-start rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-800/60"
        >
          Refresh
        </button>
      </header>

      {error && (
        <div role="alert" className="mb-4 rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {!items && !error && <p className="text-sm text-gray-500">Loading…</p>}

      {pools && pools.length > 0 && (
        <section className="mb-8">
          <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-400">
            Pool capacity
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {pools.map((p) => {
              // OCP-465 - an excluded pool is fixed-inventory by design (the
              // demo runs alone on a small box): "full" is expected, never red,
              // never a "go acquire a server" prompt.
              const excluded = !!p.excluded;
              const full = p.slotPct != null && p.slotPct >= 100;
              const hot = !excluded && ((p.slotPct ?? 0) >= poolWarnPct || (p.ramPct ?? 0) >= poolWarnPct);
              const tone = excluded ? 'neutral' : 'auto';
              return (
                <div
                  key={`${p.region}/${p.poolTag}`}
                  className={`rounded-lg border bg-white dark:bg-gray-900 p-4 shadow-sm ${
                    hot ? 'border-amber-300 dark:border-amber-800' : 'border-gray-200 dark:border-gray-800'
                  }`}
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {p.region} · {p.poolTag}
                    </span>
                    <span className="flex items-center gap-2 text-xs text-gray-500">
                      {excluded && (
                        <span className="rounded bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 text-[11px] font-medium text-gray-500 dark:text-gray-400 ring-1 ring-inset ring-gray-200 dark:ring-gray-700">
                          fixed inventory
                        </span>
                      )}
                      <span>{p.serverCount} server(s)</span>
                    </span>
                  </div>
                  {excluded ? (
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {full ? 'Full (fixed inventory) - expected.' : 'Fixed-inventory pool.'} Excluded from
                      capacity alerts, so no "acquire a server" push fires.
                    </p>
                  ) : hot ? (
                    <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
                      Filling up - acquire + register another server soon.
                      {p.alerting && p.lastAlertedAt
                        ? ` Alert sent ${formatRelative(p.lastAlertedAt)}.`
                        : ''}
                    </p>
                  ) : null}
                  <div className="mt-3 space-y-2">
                    <Bar label={`Slots (${p.usedSlots}/${p.capSlots})`} pct={p.slotPct} tone={tone} />
                    <Bar label="RAM" pct={p.ramPct} tone={tone} />
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {items && items.length === 0 && (
        <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 text-sm text-gray-500">
          No active servers registered yet.
        </div>
      )}

      {items &&
        ROLE_GROUPS.filter((g) => items.some((r) => r.serverRole === g.role)).map((g) => (
          <section key={g.role} className="mb-8">
            <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-400">
              {g.label}
            </h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {items
                .filter((r) => r.serverRole === g.role)
                .map((r) => (
                  <ServerCard key={r.id} row={r} onInstall={onInstall} />
                ))}
            </div>
          </section>
        ))}

      {installError && (
        <div role="alert" className="mt-4 rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {installError}
        </div>
      )}

      {install && <InstallModal install={install} onClose={() => setInstall(null)} />}
    </div>
  );
}

function InstallModal({ install, onClose }: { install: InstallState; onClose: () => void }) {
  const [copied, setCopied] = useTransientState(false, 2000);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(install.command);
      setCopied(true);
    } catch {
      /* clipboard blocked — operator can select manually */
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-2xl rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-xl">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
          Install agent on {install.name?.trim() || install.hostname}
        </h2>
        {/* OCP-442: show the real hostname beneath the friendly name. */}
        {install.name?.trim() && (
          <p className="mt-0.5 font-mono text-xs text-gray-500" title="Real hostname">
            {install.hostname}
          </p>
        )}
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Run this on the server as root. The secret is shown once - copy it now.
        </p>
        <pre className="mt-3 overflow-x-auto rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-3 text-xs">
          {install.command}
        </pre>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => void copy()}
            className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600"
          >
            {copied ? 'Copied' : 'Copy command'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-800/60"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

function ServerCard({ row, onInstall }: { row: FleetRow; onInstall: (r: FleetRow) => void }) {
  const dead = row.silent || !row.agentProvisioned;
  return (
    <div
      className={`rounded-lg border bg-white dark:bg-gray-900 p-4 shadow-sm ${
        dead
          ? 'border-red-300 dark:border-red-800'
          : 'border-gray-200 dark:border-gray-800'
      }`}
    >
      <div className="flex items-baseline justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate font-medium text-gray-900 dark:text-gray-100">{serverLabel(row)}</div>
          {/* OCP-442: when a friendly name is set, show the real hostname
              beneath it as muted mono secondary detail (matches OCP-433). */}
          {row.name?.trim() && (
            <div className="truncate font-mono text-[11px] text-gray-500" title="Real hostname">
              {row.hostname}
            </div>
          )}
          <div className="text-xs text-gray-500">{row.region}</div>
        </div>
        <StatusPill row={row} />
      </div>

      {!row.agentProvisioned ? (
        <div className="mt-3">
          <p className="text-xs text-gray-500">No agent installed on this server yet.</p>
          <button
            type="button"
            onClick={() => onInstall(row)}
            className="mt-2 rounded bg-brand-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-600"
          >
            Install agent
          </button>
        </div>
      ) : row.silent ? (
        <p className="mt-3 text-xs text-red-700 dark:text-red-300">
          Silent — last beacon {row.snapshotAt ? formatRelative(row.snapshotAt) : 'never'}.
        </p>
      ) : (
        <div className="mt-3 space-y-2">
          <Bar
            label="CPU"
            pct={row.cpuPct}
            detail={
              [
                row.load1 != null ? `load ${row.load1.toFixed(2)}` : null,
                // OCP-642 - per core is what the sustained load alert measures.
                row.loadPerCore != null ? `${row.loadPerCore.toFixed(1)}/core` : null,
                row.ioWaitPct != null ? `iowait ${row.ioWaitPct}%` : null,
              ]
                .filter(Boolean)
                .join(' · ') || undefined
            }
          />
          <Bar
            label="RAM"
            pct={row.ramPct}
            detail={
              [
                row.ramUsedMb != null && row.ramTotalMb != null
                  ? `${gb(row.ramUsedMb)} / ${gb(row.ramTotalMb)} GB`
                  : null,
                // OCP-642 - PSI memory pressure is the thrash signal a RAM %
                // bar cannot show (the demo box: 29 % for two hours).
                row.memPressureFullAvg60 != null ? `pressure ${row.memPressureFullAvg60.toFixed(0)}%` : null,
              ]
                .filter(Boolean)
                .join(' · ') || undefined
            }
          />
          {/* OCP-642 - swap: present on every host under 8 GB since the
              bootstrap/patch swap step; the share used tells how far past
              RAM the box is. A host without swap shows the fact plainly. */}
          {row.swapTotalMb != null && row.swapTotalMb > 0 ? (
            <Bar
              label="Swap"
              pct={row.swapPct ?? null}
              detail={`${gb(row.swapUsedMb ?? 0)} / ${gb(row.swapTotalMb)} GB`}
            />
          ) : row.swapTotalMb === 0 ? (
            <p className="text-xs text-gray-500 dark:text-gray-400">Swap: none configured</p>
          ) : null}
          {/* OCP-367 — one bar per mount so a storage box's data volume is
              visible, not just /. Falls back to the scalar mount. */}
          {(row.disks ?? []).length > 0 ? (
            (row.disks ?? []).map((m) => (
              <Bar
                key={m.mount}
                label={`Disk ${m.mount}`}
                pct={m.used_pct != null ? Math.round(m.used_pct) : m.total_gb > 0 ? Math.round((m.used_gb / m.total_gb) * 100) : null}
                detail={`${m.used_gb} / ${m.total_gb} GB${m.inode_pct != null && m.inode_pct >= 80 ? ` · inodes ${Math.round(m.inode_pct)}%` : ''}`}
              />
            ))
          ) : (
            <Bar
              label="Disk"
              pct={row.diskPct}
              detail={
                row.diskUsedGb != null && row.diskTotalGb != null
                  ? `${row.diskUsedGb} / ${row.diskTotalGb} GB`
                  : undefined
              }
            />
          )}
          {/* OCP-468 - disk saturation (%util) is separate from disk capacity
              (%used above): it is how busy the busiest physical disk was over
              the last beacon interval. This is the reliable "disk overloaded"
              signal - the CPU iowait line was noisy sub-second quantization
              until the agent moved to full-interval sampling. Old agents / the
              priming beacon omit it. */}
          {row.diskUtilPct != null && (
            <Bar label="Disk I/O" pct={row.diskUtilPct} detail="busiest disk, % of interval busy" />
          )}
          {/* OCP-622 - one VRAM bar per GPU on a model host (VRAM does not
              swap: the fullest card fails the next request). */}
          {(row.gpus ?? []).map((g) => (
            <Bar
              key={`gpu-${g.index}`}
              label={`GPU ${g.index} VRAM`}
              pct={g.vram_total_mb > 0 ? Math.round((g.vram_used_mb / g.vram_total_mb) * 100) : null}
              detail={`${(g.vram_used_mb / 1024).toFixed(1)} / ${(g.vram_total_mb / 1024).toFixed(1)} GB${g.util_pct != null ? ` · util ${Math.round(g.util_pct)}%` : ''}${g.temp_c != null ? ` · ${Math.round(g.temp_c)} °C` : ''}`}
            />
          ))}
          {/* OCP-464 - live NIC throughput (tx vs uplink) + FairFlat
              month-to-date volume. Bursts saturate the line and explode tail
              latency at 0 errors (ORB-1412), so the NIC bar is a first-class
              signal next to CPU/RAM/disk. */}
          {row.nicTxMbits != null && (
            <Bar
              label="Network (tx)"
              pct={row.nicTxPct}
              detail={
                [
                  `${fmtMbits(row.nicTxMbits)} tx`,
                  row.nicTxMbitsPeak != null ? `peak ${fmtMbits(row.nicTxMbitsPeak)}` : null,
                  row.nicRxMbits != null ? `${fmtMbits(row.nicRxMbits)} rx` : null,
                  row.uplinkMbits ? `of ${row.uplinkMbits} Mbit` : null,
                ]
                  .filter(Boolean)
                  .join(' · ') || undefined
              }
            />
          )}
          {row.volumePct != null && (
            <Bar
              label="Volume (month)"
              pct={row.volumePct}
              detail={`${fmtBytes(row.volumeTotalBytes)} / ${row.volumeIncludedGb >= 1000 ? `${Math.round(row.volumeIncludedGb / 1000)} TB` : `${row.volumeIncludedGb} GB`}`}
            />
          )}
          <div className="flex items-center justify-between pt-1 text-xs text-gray-500">
            <span>
              {row.containerRunning ?? '—'}
              {row.containerCount != null ? `/${row.containerCount}` : ''} containers
            </span>
            <span>{row.snapshotAt ? formatRelative(row.snapshotAt) : ''}</span>
          </div>
          {/* OCP-439 - who is actually consuming this box: per-tenant
              RAM/CPU (heaviest first) + a system/unattributed bucket. */}
          <TenantBreakdown tenants={row.tenants} unattributed={row.unattributed} />
        </div>
      )}
    </div>
  );
}

// OCP-439 - per-tenant consumption for one server card. Lists the heaviest
// tenants (by RAM) with their aggregated RAM/CPU + container count, plus the
// system/unattributed bucket. Renders nothing when the box has no attributable
// containers (e.g. a storage box or a never-beaconed server).
function TenantBreakdown({
  tenants,
  unattributed,
}: {
  tenants?: TenantLoad[];
  unattributed?: UnattributedLoad | null;
}) {
  const list = tenants ?? [];
  if (list.length === 0 && !unattributed) return null;
  const shown = list.slice(0, 6);
  const hiddenCount = list.length - shown.length;
  return (
    <div className="mt-2 border-t border-gray-100 dark:border-gray-800 pt-2">
      <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
        Per-tenant load
      </div>
      <ul className="space-y-1">
        {shown.map((t) => (
          <li key={t.tenantId} className="flex items-center justify-between gap-2 text-xs">
            <span className="truncate text-gray-700 dark:text-gray-300" title={t.subdomain ?? t.tenantId}>
              {t.subdomain ?? `${t.tenantId.slice(0, 8)}…`}
            </span>
            <span className="shrink-0 tabular-nums text-gray-500">
              {fmtMb(t.ramUsedMb)} · {t.cpuPct}% · {t.containerCount} ctr
            </span>
          </li>
        ))}
        {hiddenCount > 0 && (
          <li className="text-xs text-gray-400">+{hiddenCount} more tenant(s)</li>
        )}
        {unattributed && (
          <li className="flex items-center justify-between gap-2 text-xs text-gray-400">
            <span className="truncate" title="System / infra containers (proxy, maintenance, agent)">
              unattributed (system)
            </span>
            <span className="shrink-0 tabular-nums">
              {fmtMb(unattributed.ramUsedMb)} · {unattributed.cpuPct}% · {unattributed.containerCount} ctr
            </span>
          </li>
        )}
      </ul>
    </div>
  );
}

function StatusPill({ row }: { row: FleetRow }) {
  let label = 'OK';
  let cls = 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 ring-emerald-200';
  if (!row.agentProvisioned) {
    label = 'no agent';
    cls = 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 ring-gray-200';
  } else if (row.silent) {
    label = 'silent';
    cls = 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 ring-red-200';
  } else if (worst(row) >= 90) {
    label = 'critical';
    cls = 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 ring-red-200';
  } else if (worst(row) >= 75) {
    label = 'busy';
    cls = 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 ring-amber-200';
  }
  return (
    <span className={`shrink-0 rounded px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${cls}`}>
      {label}
    </span>
  );
}

function worst(row: FleetRow): number {
  return Math.max(row.cpuPct ?? 0, row.ramPct ?? 0, row.diskPct ?? 0, row.gpuVramPct ?? 0);
}

function Bar({
  label,
  pct,
  detail,
  tone = 'auto',
}: {
  label: string;
  pct: number | null;
  detail?: string;
  // OCP-465 - 'neutral' forces the brand colour regardless of value, so a
  // fixed-inventory (excluded) pool at 100% slots does not render alarm-red.
  tone?: 'auto' | 'neutral';
}) {
  const v = pct ?? 0;
  const color =
    tone === 'neutral' ? 'bg-brand-500' : v >= 90 ? 'bg-red-500' : v >= 75 ? 'bg-amber-500' : 'bg-brand-500';
  return (
    <div>
      <div className="flex items-center justify-between text-xs">
        <span className="text-gray-600 dark:text-gray-400">{label}</span>
        <span className="tabular-nums text-gray-700 dark:text-gray-300">
          {pct == null ? '—' : `${pct}%`}
          {detail ? <span className="ml-1 text-gray-400">· {detail}</span> : null}
        </span>
      </div>
      <div className="mt-1 h-1.5 w-full overflow-hidden rounded bg-gray-100 dark:bg-gray-800">
        <div className={`h-full ${color}`} style={{ width: `${Math.min(100, v)}%` }} />
      </div>
    </div>
  );
}

function gb(mb: number): string {
  return (mb / 1024).toFixed(1);
}

// OCP-439 - adaptive per-container RAM: GB above 1 GB, MB below (a single
// idle tenant stack is ~0.5 GB, so MB reads better than "0.5 GB").
function fmtMb(mb: number): string {
  return mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${mb} MB`;
}

// OCP-464 - compact NIC throughput + volume formatting.
function fmtMbits(v: number): string {
  return v >= 1000 ? `${(v / 1000).toFixed(1)} Gbit/s` : `${v.toFixed(v < 10 ? 1 : 0)} Mbit/s`;
}

function fmtBytes(bytes: number | null): string {
  if (bytes == null) return '-';
  if (bytes >= 1e12) return `${(bytes / 1e12).toFixed(2)} TB`;
  if (bytes >= 1e9) return `${(bytes / 1e9).toFixed(1)} GB`;
  if (bytes >= 1e6) return `${(bytes / 1e6).toFixed(0)} MB`;
  return `${bytes} B`;
}
